import { useState } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  UserPlus, 
  User, 
  Phone, 
  Mail, 
  Calendar,
  MapPin,
  GraduationCap,
  Briefcase,
  Car,
  Send,
  Loader2,
  CheckCircle2,
  ChevronRight,
  FileText
} from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';

const JobApplicationPage = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    email: '',
    birth_date: '',
    city: '',
    education_level: '',
    experience_years: '',
    desired_position: '',
    work_type_preference: '',
    has_driving_license: false,
    has_src_certificate: false,
    has_forklift_certificate: false,
    previous_experience: '',
    additional_notes: ''
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const requiredFields = ['full_name', 'phone', 'email', 'birth_date', 'city', 'education_level', 'experience_years', 'desired_position', 'work_type_preference', 'previous_experience'];
    const missingFields = requiredFields.filter(field => !formData[field]);
    
    if (missingFields.length > 0) {
      toast.error('Lütfen tüm zorunlu alanları doldurun');
      return;
    }

    setLoading(true);
    try {
      await api.post('/job-application', formData);
      setSubmitted(true);
      toast.success('Başvurunuz başarıyla alındı!');
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error('Bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Layout>
        <div className="min-h-[70vh] flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 py-20">
          <div className="text-center max-w-lg mx-auto px-4">
            <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Başvurunuz Alındı!
            </h1>
            <p className="text-slate-600 mb-8">
              İş başvurunuz başarıyla iletildi. İnsan kaynakları ekibimiz başvurunuzu değerlendirdikten sonra sizinle iletişime geçecektir.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/">
                <Button variant="outline" className="gap-2">
                  Ana Sayfaya Dön
                </Button>
              </Link>
              <Button 
                onClick={() => {
                  setSubmitted(false);
                  setFormData({
                    full_name: '',
                    phone: '',
                    email: '',
                    birth_date: '',
                    city: '',
                    education_level: '',
                    experience_years: '',
                    desired_position: '',
                    work_type_preference: '',
                    has_driving_license: false,
                    has_src_certificate: false,
                    has_forklift_certificate: false,
                    previous_experience: '',
                    additional_notes: ''
                  });
                }}
                className="bg-[#005CB2] hover:bg-[#004B92] gap-2"
              >
                Yeni Başvuru Yap
              </Button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Breadcrumb */}
      <div className="bg-slate-50 border-b">
        <div className="eta-container py-4">
          <div className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-[#005CB2] hover:underline">Ana Sayfa</Link>
            <ChevronRight className="w-4 h-4 text-slate-400" />
            <span className="text-slate-600">İş Başvurusu</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#005CB2] to-[#003d75] py-16">
        <div className="eta-container text-center text-white">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-6">
            <UserPlus className="w-8 h-8" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            İş Başvuru Formu
          </h1>
          <p className="text-white/80 max-w-2xl mx-auto text-lg">
            Kariyer fırsatları için aşağıdaki formu doldurarak başvurunuzu yapabilirsiniz.
            Aday havuzumuza katılın, size uygun pozisyonlar için sizinle iletişime geçelim!
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 bg-slate-50">
        <div className="eta-container">
          <div className="max-w-3xl mx-auto">
            <Card className="shadow-xl border-0">
              <CardHeader className="bg-slate-50 border-b">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <FileText className="w-6 h-6 text-[#005CB2]" />
                  Başvuru Bilgileri
                </CardTitle>
              </CardHeader>
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-8">
                  {/* Personal Information */}
                  <div className="space-y-6">
                    <h3 className="font-semibold text-slate-900 flex items-center gap-2 pb-2 border-b">
                      <User className="w-5 h-5 text-[#005CB2]" />
                      Kişisel Bilgiler
                    </h3>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="full_name" className="font-semibold">
                          Ad Soyad <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="full_name"
                          name="full_name"
                          value={formData.full_name}
                          onChange={handleChange}
                          placeholder="Adınız ve soyadınız"
                          required
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="birth_date" className="font-semibold flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          Doğum Tarihi <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="birth_date"
                          name="birth_date"
                          type="date"
                          value={formData.birth_date}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="font-semibold flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          Telefon Numarası <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="05XX XXX XX XX"
                          required
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="email" className="font-semibold flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          E-Posta Adresi <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="ornek@email.com"
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="city" className="font-semibold flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        Yaşadığınız Şehir <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        placeholder="Örn: İstanbul"
                        required
                      />
                    </div>
                  </div>

                  {/* Education & Experience */}
                  <div className="space-y-6">
                    <h3 className="font-semibold text-slate-900 flex items-center gap-2 pb-2 border-b">
                      <GraduationCap className="w-5 h-5 text-[#005CB2]" />
                      Eğitim ve Deneyim
                    </h3>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label className="font-semibold">
                          Eğitim Durumu <span className="text-red-500">*</span>
                        </Label>
                        <Select value={formData.education_level} onValueChange={(v) => handleSelectChange('education_level', v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Seçiniz" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="İlkokul">İlkokul</SelectItem>
                            <SelectItem value="Ortaokul">Ortaokul</SelectItem>
                            <SelectItem value="Lise">Lise</SelectItem>
                            <SelectItem value="Ön Lisans">Ön Lisans</SelectItem>
                            <SelectItem value="Lisans">Lisans</SelectItem>
                            <SelectItem value="Yüksek Lisans">Yüksek Lisans</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="font-semibold">
                          İş Deneyimi <span className="text-red-500">*</span>
                        </Label>
                        <Select value={formData.experience_years} onValueChange={(v) => handleSelectChange('experience_years', v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Seçiniz" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Deneyimsiz">Deneyimsiz</SelectItem>
                            <SelectItem value="1 yıldan az">1 yıldan az</SelectItem>
                            <SelectItem value="1-2 yıl">1-2 yıl</SelectItem>
                            <SelectItem value="3-5 yıl">3-5 yıl</SelectItem>
                            <SelectItem value="5-10 yıl">5-10 yıl</SelectItem>
                            <SelectItem value="10+ yıl">10+ yıl</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Position Preferences */}
                  <div className="space-y-6">
                    <h3 className="font-semibold text-slate-900 flex items-center gap-2 pb-2 border-b">
                      <Briefcase className="w-5 h-5 text-[#005CB2]" />
                      Pozisyon Tercihleri
                    </h3>
                    
                    <div className="space-y-2">
                      <Label htmlFor="desired_position" className="font-semibold">
                        Başvurmak İstediğiniz Pozisyon <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="desired_position"
                        name="desired_position"
                        value={formData.desired_position}
                        onChange={handleChange}
                        placeholder="Örn: Depo Elemanı, Forklift Operatörü, Paketleme Elemanı"
                        required
                      />
                    </div>

                    <div className="space-y-3">
                      <Label className="font-semibold">
                        Çalışma Şekli Tercihi <span className="text-red-500">*</span>
                      </Label>
                      <RadioGroup 
                        value={formData.work_type_preference} 
                        onValueChange={(value) => setFormData(prev => ({ ...prev, work_type_preference: value }))}
                        className="flex flex-wrap gap-6"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="Tam Zamanlı" id="wt_full" />
                          <Label htmlFor="wt_full" className="cursor-pointer">Tam Zamanlı</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="Yarı Zamanlı" id="wt_part" />
                          <Label htmlFor="wt_part" className="cursor-pointer">Yarı Zamanlı</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="Her ikisi" id="wt_both" />
                          <Label htmlFor="wt_both" className="cursor-pointer">Her ikisi de olur</Label>
                        </div>
                      </RadioGroup>
                    </div>
                  </div>

                  {/* Certificates */}
                  <div className="space-y-6">
                    <h3 className="font-semibold text-slate-900 flex items-center gap-2 pb-2 border-b">
                      <Car className="w-5 h-5 text-[#005CB2]" />
                      Sertifika ve Belgeler
                    </h3>
                    
                    <div className="grid md:grid-cols-3 gap-4">
                      <label className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors">
                        <Checkbox
                          id="has_driving_license"
                          name="has_driving_license"
                          checked={formData.has_driving_license}
                          onCheckedChange={(checked) => setFormData(prev => ({ ...prev, has_driving_license: checked }))}
                        />
                        <span className="text-sm font-medium">Ehliyet var</span>
                      </label>
                      
                      <label className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors">
                        <Checkbox
                          id="has_src_certificate"
                          name="has_src_certificate"
                          checked={formData.has_src_certificate}
                          onCheckedChange={(checked) => setFormData(prev => ({ ...prev, has_src_certificate: checked }))}
                        />
                        <span className="text-sm font-medium">SRC Belgesi</span>
                      </label>
                      
                      <label className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors">
                        <Checkbox
                          id="has_forklift_certificate"
                          name="has_forklift_certificate"
                          checked={formData.has_forklift_certificate}
                          onCheckedChange={(checked) => setFormData(prev => ({ ...prev, has_forklift_certificate: checked }))}
                        />
                        <span className="text-sm font-medium">Forklift Belgesi</span>
                      </label>
                    </div>
                  </div>

                  {/* Additional Info */}
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="previous_experience" className="font-semibold">
                        Daha Önce Çalıştığınız Yerler <span className="text-red-500">*</span>
                      </Label>
                      <Textarea
                        id="previous_experience"
                        name="previous_experience"
                        value={formData.previous_experience}
                        onChange={handleChange}
                        placeholder="Daha önce çalıştığınız firma ve pozisyonları kısaca yazınız"
                        rows={4}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="additional_notes" className="font-semibold">
                        Ek Notlar (İsteğe bağlı)
                      </Label>
                      <Textarea
                        id="additional_notes"
                        name="additional_notes"
                        value={formData.additional_notes}
                        onChange={handleChange}
                        placeholder="Eklemek istediğiniz bilgiler varsa yazabilirsiniz"
                        rows={3}
                      />
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="pt-4">
                    <Button 
                      type="submit" 
                      disabled={loading}
                      className="w-full bg-[#005CB2] hover:bg-[#004B92] text-white py-6 text-lg font-semibold gap-2"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Gönderiliyor...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          Başvuruyu Gönder
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default JobApplicationPage;
